import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackMetaEvent } from "src/utils/metaCapi";

/**
 * Meta (Facebook) pixel.
 *
 * Mounted once at the app root (src/Tracking.js) and persists for the lifetime
 * of the session. Because this is a SPA, the browser never reloads on an
 * in-app navigation, so the pixel has to re-fire PageView itself on every
 * client-side route change. Meta attributes each event to document.location at
 * the moment it fires, so a single fire at bootstrap reports only the landing
 * URL — visitors who arrive on the home page and navigate to /poker never
 * produce a PageView carrying a /poker URL, which is what URL-based
 * retargeting audiences are built from.
 *
 * ViewContent (a standard event) fires alongside PageView on every page, with
 * the same timing.
 *
 * PageView and ViewContent are keyed on pathname only. Query strings are
 * deliberately ignored so that in-page filters (tournament lists, calendars)
 * don't inflate the event counts; the event URL Meta records still includes
 * the full URL.
 *
 * PokerPageView is a custom event for retargeting that does not depend on
 * Meta's stored URL field. It fires on /poker and /fr/poker (and subpaths)
 * only — not /poker-table-filming-policy or /promotions/poker.
 *
 * Each event gets its own eventID and a same-origin CAPI POST (see
 * src/utils/metaCapi.js) so Pixel + server events can be deduplicated.
 */
const isPokerPath = (pathname) =>
  pathname === "/poker" ||
  pathname.startsWith("/poker/") ||
  pathname === "/fr/poker" ||
  pathname.startsWith("/fr/poker/");

const FacebookPixel = ({ pixelId, locale, events = ["PageView", "ViewContent"] }) => {
  const location = useLocation();
  // Whether fbq has been initialized, so setup runs once for the session
  // rather than again on every navigation.
  const initialized = useRef(false);

  useEffect(() => {
    if (!pixelId) return;

    if (!initialized.current) {
      // Ensure fbq is loaded once (the f.fbq guard makes this a no-op if the
      // base script is already present).
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/" + locale + "/fbevents.js");

      window.fbq("init", pixelId);
      initialized.current = true;
    }

    // Fires on the initial load and again on every client-side route change.
    // Deferred one frame so react-helmet-async can commit <title>/<meta>
    // before Meta scrapes the DOM. Cleanup cancels the frame if the path
    // changes again first.
    const raf = requestAnimationFrame(() => {
      events.forEach((event) => trackMetaEvent(event));
      if (isPokerPath(location.pathname)) {
        trackMetaEvent("PokerPageView", { custom: true });
      }
    });

    return () => cancelAnimationFrame(raf);
    // events/locale are static config and are deliberately out of the deps:
    // `events` is a new array on every render and would fire a PageView on
    // every re-render, and `locale` only feeds the one-time script URL above.
  }, [pixelId, location.pathname]);

  return null;
};

export default FacebookPixel;
